import React, { useMemo } from 'react';

// Components
import { ComparisonsSection } from '.';
import { ComparisonsTableFields } from '@/views/components/tables/ComparisonsTable';

// Enums
import { COMPARISONS_FILTER } from './ComparisonsFilterNavigation';
import { CONTACT_LAYOUT, getContactLayout } from '@/utils/contacts';

// Types
import Comparison from '@/types/comparison';
import GenericContact from '@/types/generic-contact';
import ReducedComparison from '@/types/reduced-comparison';
import User from '@/types/user';
import { ComparisonsListState } from '@/redux/modules/types/comparison';

// Utils
import { formatMessage } from '@/utils/i18n';

type Props = {
  comparisons: {
    [key in COMPARISONS_FILTER]: ComparisonsListState;
  };
  comparisonsFilter: COMPARISONS_FILTER;
  filterComponent?: JSX.Element;
  loadComparisons: (query?: string, useNextToken?: boolean) => Promise<ReducedComparison[]>;
  onCreateBMOComparison?: () => void;
  onCreateComparison: () => void;
  onDeleteComparison: (comparison: ReducedComparison) => void;
  onOpenComparison: (comparison: ReducedComparison) => void;
  onSaveComparison: (id: string, name: string, contact?: GenericContact) => Promise<Comparison>;
  options?: {
    showCreateComparison?: boolean;
    showStartBMOComparison?: boolean;
    tableIsSortable?: boolean;
    showHeader?: boolean;
    hideEmptyStateCard?: boolean;
  };
  user: User;
};

/**
 *  ComparisonsList component depending on the filter selected in the ComparisonsFilterNavigation
 *  This is part of the Correlate light theme update.
 *  Note that it is also used in the fact-set appMode (/fact-set).
 *
 *  FYI options : tableIsSortable
 *  are only for the appMode version of fact-set.
 *
 *  TODO : Remove this component and move it's logic to Comparisons.tsx and Landing.tsx
 *  This code is only needed by the light version and the fact set landing page.
 *  It made sense before to have this component in the middle of comparisons.tsx and ComparisonsSections.tsx
 *  because Comparisons.tsx was handling classic and light version at the same time,
 *  but now that we use wrappers, Comparisons.tsx can set it’s own props to renders the good ComparisonsSection.
 */
const ComparisonsList: React.FC<Props> = ({
  comparisons,
  comparisonsFilter,
  filterComponent,
  loadComparisons,
  onCreateBMOComparison,
  onCreateComparison,
  onDeleteComparison,
  onOpenComparison,
  onSaveComparison,
  options,
  user,
}: Props) => {
  const classes = 'comparisons-list flex-fill';

  const tableFields = useMemo(() => {
    switch (comparisonsFilter) {
      case COMPARISONS_FILTER.COMPLIANCE:
        return [ComparisonsTableFields.Name, ComparisonsTableFields.SavedTo, ComparisonsTableFields.DateCreated, ComparisonsTableFields.Open];
      case COMPARISONS_FILTER.DRAFTS:
        return [ComparisonsTableFields.Name, ComparisonsTableFields.LastModified, ComparisonsTableFields.Open, ComparisonsTableFields.Actions];
      case COMPARISONS_FILTER.ASSIGNED:
      case COMPARISONS_FILTER.RECENT:
      default:
        return [
          ComparisonsTableFields.Name,
          ComparisonsTableFields.SavedTo,
          ComparisonsTableFields.LastModified,
          ComparisonsTableFields.Open,
          ComparisonsTableFields.Actions,
        ];
    }
  }, [comparisonsFilter]);

  const searchPlaceholder = useMemo(() => {
    if ([COMPARISONS_FILTER.ASSIGNED, COMPARISONS_FILTER.RECENT].includes(comparisonsFilter)) {
      const layout = getContactLayout(user);
      const searchContactPlaceholder =
        layout === CONTACT_LAYOUT.CLIENT
          ? formatMessage('ComparisonHome.SearchComparisonsClientPlaceholder')
          : formatMessage('ComparisonHome.SearchComparisonsContactPlaceholder');

      return searchContactPlaceholder;
    }

    return formatMessage('ComparisonHome.SearchComparisonsPlaceholder');
  }, [comparisonsFilter, user]);

  return (
    <ComparisonsSection
      className={classes}
      comparisons={comparisons[comparisonsFilter]?.list || []}
      hasNoComparisons={Object.keys(comparisons).reduce(
        (acc, key) => acc && (!comparisons[key as COMPARISONS_FILTER]?.list.length || key === COMPARISONS_FILTER.COMPLIANCE),
        true,
      )}
      filterComponent={filterComponent}
      selectedFilter={comparisonsFilter}
      hasMore={!!comparisons[comparisonsFilter]?.nextToken}
      key={comparisonsFilter}
      loadComparisons={loadComparisons}
      onCreateBMOComparison={onCreateBMOComparison}
      onCreateComparison={onCreateComparison}
      onDeleteComparison={onDeleteComparison}
      onOpenComparison={onOpenComparison}
      onSaveComparison={onSaveComparison}
      responsive
      searchPlaceholder={searchPlaceholder}
      showCreateComparison={options?.showCreateComparison ?? false}
      forceHideEmptyStateCard={options?.hideEmptyStateCard}
      showHeader={options?.showHeader}
      showStartBMOComparison={options?.showStartBMOComparison ?? false}
      sortable={options?.tableIsSortable}
      tableFields={tableFields}
      title={formatMessage('ComparisonHome.PageTitle')}
      i18nTitle="ComparisonHome.PageTitle"
      user={user}
    />
  );
};

export default ComparisonsList;
